// src/routes/AppRoutes.tsx

import React, { lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import LoginPage from '@/views/Login/LoginPage';
import NotFound from '@/views/NotFound/NotFound';
import KYCRedirect from '@/views/KYC/KYCRedirect';
import { NAVBAR_SOURCES } from '@/components/AppComponents/Sidebar/NavBar/NavBarSources';
import ForgotPassword from '@/views/Login/ForgotPassword';
import ResetPassword from '@/views/Login/ResetPassword';
import CreatePassword from '@/views/Login/CreatePassword';
import SSOTransferView from '@/views/SSOTransfer/SSOTransferView';
import ScrollToTop from '@/shared/lib/scrollToTop';
import HomeRedirect from '@/routes/HomeRedirect';

// lazy load for protected views

const Dashboard = lazy(() => import('@/views/Dashboard'));
const Kyc = lazy(() => import('@/views/KYC'));
const Settings = lazy(() => import('@/views/Settings'));
const InvoicesLedger = lazy(() => import('@/views/Funding/InvoicesLedger'));
const Revenues = lazy(() => import('@/views/Funding/Revenues'));
const Commissions = lazy(() => import('@/views/Funding/Commissions'));
const Activity = lazy(() => import('@/views/Funding/Activity'));
const Documents = lazy(() => import('@/views/Funding/Documents'));
const FundingRules = lazy(() => import('@/views/Sources/FundingRules'));
const CommercialTerms = lazy(() => import('@/views/Sources/CommercialTerms'));
const BusinessDetails = lazy(() => import('@/views/Account/BusinessDetails'));
const BankAccounts = lazy(() => import('@/views/Account/BankAccounts'));
const Users = lazy(() => import('@/views/Account/Users'));
const Guest = lazy(() => import('@/views/Guest'));
const UIExamples = lazy(() => import('@/domains/example/views/UIexamples'));

const AppRoutes = () => {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* Public routes */}
        <Route path={NAVBAR_SOURCES.login} element={<LoginPage />} />
        <Route
          path={NAVBAR_SOURCES.forgotPassword}
          element={<ForgotPassword />}
        />
        <Route
          path={NAVBAR_SOURCES.resetPassword}
          element={<ResetPassword />}
        />
        <Route
          path={NAVBAR_SOURCES.createPassword}
          element={<CreatePassword />}
        />
        <Route
          path={NAVBAR_SOURCES.ssoTransfer}
          element={<SSOTransferView />}
        />
        <Route path="/example" element={<UIExamples />} />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<HomeRedirect />} />
          <Route path={NAVBAR_SOURCES.kyc} element={<Kyc />} />
          <Route path={NAVBAR_SOURCES.dashboard} element={<Dashboard />} />
          <Route path={NAVBAR_SOURCES.guest} element={<Guest />} />
          {/* Settings */}
          <Route path={NAVBAR_SOURCES.settings} element={<Settings />} />
          {/* Funding */}
          <Route path={NAVBAR_SOURCES.commissions} element={<Commissions />} />
          <Route
            path={NAVBAR_SOURCES.invoicesLedger}
            element={<InvoicesLedger />}
          />
          <Route path={NAVBAR_SOURCES.revenues} element={<Revenues />} />
          <Route path={NAVBAR_SOURCES.activity} element={<Activity />} />
          <Route path={NAVBAR_SOURCES.documents} element={<Documents />} />
          {/* Revenue Sources */}
          <Route
            path={NAVBAR_SOURCES.fundingRules}
            element={<FundingRules />}
          />
          <Route
            path={NAVBAR_SOURCES.commercialTerms}
            element={<CommercialTerms />}
          />
          {/* Account */}
          <Route
            path={NAVBAR_SOURCES.businessDetails}
            element={<BusinessDetails />}
          />
          <Route
            path={NAVBAR_SOURCES.bankAccounts}
            element={<BankAccounts />}
          />
          <Route path={NAVBAR_SOURCES.users} element={<Users />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
        <Route path="/kyc-redirect" element={<KYCRedirect />} />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
